import React, { useEffect, useState } from "react";
import parse from "html-react-parser";
import { connect } from "react-redux";
import className from "./style.module.scss";
import { updateSpreadsheetOverwrites } from "../Spreadsheet/Reducers/actions";

/**
 * StepAssist Component
 * Displays assistance or feedback for a specific step based on interactions and criteria.
 */
const StepAssist = (props) => {
  const {
    assist = {}, // Assistance data
    step, // Current step data
    isCurrentStep, // Whether this is the current step
    spreadsheet, // Spreadsheet data
  } = props;

  const [offerHint, setOfferHint] = useState(assist.offerHint);

  /**
   * Effect to automatically apply assist overrides when conditions are met.
   */
  useEffect(() => {
    if (!offerHint && shouldDisplayAssist(step, assist)) {
      applyAssistOverrides(assist.revealHintActions);
    }
  }, [offerHint, step.sheetChanges, step.unsanitisedSheetChanges]);

  /**
   * Determines whether the assist should be displayed based on step and assist criteria.
   * @param {Object} step - The current step.
   * @param {Object} assist - The assist data.
   * @returns {boolean} Whether the assist should be displayed.
   */
  const shouldDisplayAssist = (step, assist) => {
    if (!isCurrentStep) {
      return false;
    }

    const hasCriteria = assist.specificCriteria.length > 0;
    const meetsThreshold = assist.cellSpecific
      ? assist.interactionThreshold <= step.sheetChanges
      : assist.interactionThreshold <= step.unsanitisedSheetChanges;

    return meetsThreshold && (!hasCriteria || checkAssistCriteria(assist));
  };

  /**
   * Checks if the assist criteria are met.
   * @param {Object} assist - The assist data.
   * @returns {boolean} Whether the criteria are met.
   */
  const checkAssistCriteria = (assist) => {
    const stepCriteria = {
      passCriteria: assist.specificCriteria,
      onlyOnePassNeeded: assist.onlyOnePassNeeded,
      negativePass: assist.negativePass,
    };
    return props.isPassing(stepCriteria);
  };

  /**
   * Applies the assist overrides by updating the queued overwrites in the spreadsheet.
   * @param {Array} overwrites - List of overwrite keys to apply.
   */
  const applyAssistOverrides = (overwrites) => {
    const updatedQueuedOverwrites = [...(spreadsheet.queuedOverwrites || [])];
    overwrites?.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(spreadsheet.overwrites, key)) {
        updatedQueuedOverwrites.push(spreadsheet.overwrites[key]);
      }
    });
    props.updateSpreadsheetOverwrites(updatedQueuedOverwrites);
  };

  return (
    <div
      className={className.taskFeedback}
      style={{ display: shouldDisplayAssist(step, assist) ? null : "none" }}
    >
      {!offerHint ? (
        <div aria-live="polite">
          <p>{parse(assist.message)}</p>
          {assist.offerAutocomplete.length > 0 && (
            <span
              className={className.taskFeedbackBtn}
              role="button"
              aria-label={assist.autocompleteMessage}
              tabIndex={0}
              onClick={() => applyAssistOverrides(assist.offerAutocomplete)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  applyAssistOverrides(assist.offerAutocomplete);
                }
              }}
            >
              {parse(assist.autocompleteMessage)}
            </span>
          )}
        </div>
      ) : (
        <span
          className={className.hintBtn}
          role="button"
          alt-text="Reveal hint"
          tabIndex={0}
          onClick={() => setOfferHint(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setOfferHint(false);
            }
          }}
        >
          Reveal Hint
        </span>
      )}
    </div>
  );
};

/**
 * Maps Redux state to component props.
 * @param {Object} state - The Redux state.
 * @param {Object} props - The component's own props.
 * @returns {Object} The mapped props.
 */
function mapStateToProps(state, props) {
  return {
    assist: props.assist,
    step: props.step,
    isCurrentStep: props.isCurrentStep,
    spreadsheet: state.spreadsheet.spreadsheetData,
    isPassing: props.isPassing,
  };
}

/**
 * Maps Redux actions to component props.
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {Object} The mapped actions.
 */
function mapDispatchToProps(dispatch) {
  return {
    updateSpreadsheetOverwrites: (data) =>
      dispatch(updateSpreadsheetOverwrites(data)),
  };
}

// Connect the component to the Redux store
const mapped_StepAssist = connect(
  mapStateToProps,
  mapDispatchToProps
)(StepAssist);

export { mapped_StepAssist as StepAssist };
